import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { StepService } from '@/services/step.service'
import { FieldRuleService } from '@/services/field-rule.service'
import { ValueRuleService } from '@/services/value-rule.service'
import { useGlobalStore } from '@/stores/globalStore.ts'
import { useMappingStore } from '@/stores/mappingStore.ts'
import { DataSchemaService } from '@/services/data-schema.service'
import { ConfigService } from '@/services/config.service.ts'
import { generateField } from '@/services/helper.service.ts'
import { useCalculationStore } from '@/stores/calculationStore.ts'
import { CalculationService } from '@/services/calculation.service.ts'
import type { ApplicationSchema, Step, FormField, Field, ContentSchema } from '@/ts/types/schema.types'
import type { Config } from '@/ts/types/config.types'
import { i18n } from '@/plugins/i18n'

export const useFormStore = defineStore('form', () => {
  const GlobalStore = useGlobalStore()
  const MappingStore = useMappingStore()
  const CalculationStore = useCalculationStore()

  const _stepCounter = ref(0)
  const _applicationSchema = ref<ApplicationSchema | null>(null)

  const applicationSchema = computed(() => _applicationSchema.value)
  const currentStep = computed(() => _applicationSchema.value?.form_steps.find((step: Step) => step.step_id === _stepCounter.value))
  const stepCounter = computed(() => _stepCounter.value)
  const steps = computed(() => _applicationSchema.value?.form_steps || [])
  const formLocale = computed(() => _applicationSchema.value?.locale)
  const formSchemaLoading = computed(() => currentStep.value?.step_loading)

  const fieldById = (fieldId: string) =>
    computed<FormField | undefined>(() => {
      const stepId = parseInt(fieldId.split('-')[0])
      let result
      StepService.findStep(steps.value, stepId, (step: Step) => {
        StepService.findField(step.form_schema, fieldId, (field: FormField) => {
          result = field
        })
      })
      return result
    })

  async function fetchApplicationSchema(alias: string = 'default', config?: Config, contentSchema?: ContentSchema) {
    const locale = String(i18n.global.locale)
    GlobalStore.updateLoadingState(true)
    try {
      const res = await DataSchemaService.getApplicationSchema(locale, alias)
      if (res?.data?.form_steps && res?.data?.mappings) {
        if (contentSchema) res.data.content_schema = contentSchema
        await setApplicationSchemaData(res?.data)
        await fetchConfig(alias, config)
        await CalculationService.updateAllCalculations()
      }
      GlobalStore.updateLoadingState(false)
    } catch (error: any) {
      console.log(error)
      return error
    }
  }

  async function fetchConfig(alias: string, config?: Config) {
    try {
      const res = ConfigService.getConfig(alias, config)
      if (res && res.data) {
        CalculationStore.initConfig(res?.data)
        await ConfigService.applyConfigToForm(res?.data, MappingStore, findFormField, generateField)
      }
    } catch (error: any) {
      console.log(error)
      return error
    }
  }

  function resetDynamicForm(): void {
    _stepCounter.value = 0
    _applicationSchema.value = null
  }

  async function setApplicationSchemaData(data: ApplicationSchema): Promise<void> {
    _applicationSchema.value = data || null
    MappingStore.initMappings(data.mappings)
    updateCurrentStep(1)
  }

  function updateCurrentStep(id: number): void {
    StepService.findStep(steps.value, id, (formStep: Step) => (_stepCounter.value = formStep.step_id))
  }

  async function checkFormFieldChanged(field: Field): Promise<void> {
    await updateFormFieldValue(field)
  }

  function updateStepStatus(status: boolean, stepId: number | undefined): void {
    StepService.findStep(_applicationSchema.value?.form_steps, stepId, (formStep: Step) => {
      formStep.valid = status
    })
  }

  async function updateFormFieldValue(field: Field): Promise<void> {
    await findFormField(field, async (formField: FormField): Promise<void> => {
      if (formField) {
        formField.config.value = field.value
        try {
          formField.config.valid = await checkFormFieldStatusIsValid(FieldRuleService.getRules(formField.config.field_rules), field.value)
          await executeFormFieldValueRules(ValueRuleService.getRules(formField.config.value_rules), field.value)
        } catch (error) {
          console.info(error)
        }
      }
    })
  }

  async function findFormField(field: Field, callback: Function): Promise<void> {
    if (field && field.stepId && field.fieldId) {
      StepService.findStep(_applicationSchema.value?.form_steps, field.stepId, (formStep: Step) => {
        StepService.findField(formStep.form_schema, field.fieldId, callback)
      })
    }
  }

  function validateFormSchemaStatus(status: boolean) {
    GlobalStore.updateLoadingState(true)
    const prevStatus = currentStep.value?.valid
    if (status && currentStep.value)
      currentStep.value?.form_schema.forEach((field: any) => {
        if (!field.component?.includes('Content') && field.config.field_rules.length > 0 && !field.config.hide && !field.config.valid)
          field.config.valid = true
      })
    updateStepStatus(status, currentStep.value?.step_id)
    if (!prevStatus && currentStep.value?.valid) {
      CalculationService.updateAllCalculations()
    }
    GlobalStore.updateLoadingState(false)
  }

  async function executeFormFieldValueRules(rules: any, fieldValue: any) {
    if (rules.length > 0) {
      for (const rule of rules) {
        await rule(fieldValue)
      }
    }
  }

  async function checkFormFieldStatusIsValid(rules: Array<Function>, fieldValue: any): Promise<any> {
    return new Promise((resolve) => {
      if (rules.length > 0) {
        const preparedRules = rules.map((rule: Function) => rule(fieldValue))
        Promise.allSettled(preparedRules)
          .then((results) => {
            results.forEach((res) => {
              if (res.status !== 'fulfilled' || res?.value !== true) resolve(false)
            })
          })
          .finally(() => resolve(true))
      } else {
        resolve(true)
      }
    })
  }

  function toggleExtendedSettings(): void {
    currentStep.value?.form_schema.forEach((field: FormField) => {
      if (field.config.extended_setting && !field.config.hidden_by_config) {
        field.config.hide = !field.config.hide
      }
    })
  }

  return {
    currentStep,
    stepCounter,
    steps,
    formSchemaLoading,
    applicationSchema,
    formLocale,
    fieldById,
    fetchApplicationSchema,
    validateFormSchemaStatus,
    checkFormFieldChanged,
    resetDynamicForm,
    toggleExtendedSettings
  }
})
